import { useState } from 'react';
import { db } from '../../lib/firebase';
import { removeDoc, saveDoc } from '../../lib/firestore-write';
import { useOrderedCol } from '../../lib/hooks';
import type { ChapterDoc, CourseDoc, ResourceDoc, SectionDoc } from '../../lib/data-model';
import { ConfirmDialog, EmptyState, Loading, toast } from '../ui';
import { AdminToolbar, DeleteBtn, EditBtn, HiddenBtn, ItemRow, NewBtn, SwapBar, errOf } from './ListAdmin';
import { ChapterEditor } from './ChapterEditor';

const sortOrder = <T extends { order: number }>(arr: T[]) => [...arr].sort((a, b) => a.order - b.order);

export function ChaptersTab() {
  const courses = useOrderedCol<CourseDoc>('courses');
  const [selected, setSelected] = useState<string>('');
  const courseKey = selected || courses.data[0]?.id || '';

  const chapters = useOrderedCol<ChapterDoc>('chapters', { where: ['courseId', courseKey], enabled: !!courseKey });
  const allSections = useOrderedCol<SectionDoc>('sections');
  const allResources = useOrderedCol<ResourceDoc>('resources');

  const [openCh, setOpenCh] = useState<string | null>(null);
  const [view, setView] = useState<{ id?: string } | null>(null);
  const [deleting, setDeleting] = useState<{ kind: 'chapter' | 'section' | 'resource'; id: string; name: string } | null>(null);
  const [query, setQuery] = useState('');

  const sectionsOf = (chapterId: string) => sortOrder(allSections.data.filter(s => s.chapterId === chapterId));
  const resourcesOf = (sectionId: string) => sortOrder(allResources.data.filter(r => r.sectionId === sectionId));

  const q = query.trim().toLowerCase();
  const chapHits = (c: ChapterDoc & { id: string }) => !q || `${c.num} ${c.title} ${c.blurb}`.toLowerCase().includes(q);
  const secHits = (s: SectionDoc & { id: string }) => !q || `${s.title} ${s.summary}`.toLowerCase().includes(q);
  const chaptersShown = sortOrder(chapters.data).filter(c => chapHits(c) || sectionsOf(c.id).some(secHits));
  const secTotal = chapters.data.reduce((n, c) => n + sectionsOf(c.id).length, 0);

  if (courses.loading) return <Loading label="Loading courses…" />;
  if (courses.data.length === 0)
    return <EmptyState glyph="▤" title="No courses yet" body="Create a course in the Courses tab before adding chapters." />;

  if (view) {
    const courseName = courses.data.find(c => c.id === courseKey)?.name ?? courseKey;
    return (
      <div className="panel">
        <div className="admin-wrap">
          <ChapterEditor
            courseId={courseKey}
            courseName={courseName}
            chapterId={view.id ?? null}
            loading={chapters.loading}
            chapters={chapters.data}
            sections={allSections.data}
            resources={allResources.data}
            onBack={() => setView(null)}
            onCreated={id => setView({ id })}
          />
        </div>
      </div>
    );
  }

  const toggleChapter = (c: ChapterDoc & { id: string }) =>
    saveDoc(db, 'chapters', c.id, { ...c, hidden: !c.hidden })
      .then(() => toast('Saved', 'ok'))
      .catch(e => toast(errOf(e), 'err'));

  const toggleSection = (s: SectionDoc & { id: string }) =>
    saveDoc(db, 'sections', s.id, { ...s, hidden: !s.hidden })
      .then(() => toast('Saved', 'ok'))
      .catch(e => toast(errOf(e), 'err'));

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      if (deleting.kind === 'chapter') {
        for (const s of sectionsOf(deleting.id)) {
          for (const r of resourcesOf(s.id)) await removeDoc(db, 'resources', r.id);
          await removeDoc(db, 'sections', s.id);
        }
        await removeDoc(db, 'chapters', deleting.id);
      } else if (deleting.kind === 'section') {
        for (const r of resourcesOf(deleting.id)) await removeDoc(db, 'resources', r.id);
        await removeDoc(db, 'sections', deleting.id);
      } else {
        await removeDoc(db, 'resources', deleting.id);
      }
      toast('Deleted', 'ok');
      if (openCh && deleting.kind === 'chapter') setOpenCh(null);
    } catch (e) {
      toast(errOf(e), 'err');
    }
  };

  const cascadeNote = (c: ChapterDoc & { id: string }) => {
    const secs = sectionsOf(c.id);
    const res = secs.reduce((n, s) => n + resourcesOf(s.id).length, 0);
    return `${secs.length} section${secs.length === 1 ? '' : 's'} and ${res} resource${res === 1 ? '' : 's'} will be permanently deleted.`;
  };

  return (
    <div className="panel">
      <div className="admin-wrap">
        <AdminToolbar label={`${chapters.data.length} CH · ${secTotal} SECTIONS`} query={query} onQuery={setQuery}>
          <div className="field" style={{ margin: 0, maxWidth: 240 }}>
            <label>Course</label>
            <select value={courseKey} onChange={e => { setSelected(e.target.value); setOpenCh(null); setQuery(''); }}>
              {courses.data.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name} · {c.code}
                </option>
              ))}
            </select>
          </div>
          <NewBtn onClick={() => setView({})}>+ New chapter</NewBtn>
        </AdminToolbar>

        {chapters.loading ? (
          <Loading label="Loading chapters…" />
        ) : chapters.data.length === 0 ? (
          <EmptyState glyph="◈" title="No chapters" body="Add your first chapter for this course." />
        ) : chaptersShown.length === 0 ? (
          <EmptyState glyph="⌕" title="No matches" body={`Nothing in this course matches “${query}”. Try another title.`} />
        ) : (
          <div className="admin-list">
            {chaptersShown.map(c => {
              const secs = sectionsOf(c.id).filter(s => chapHits(c) || secHits(s));
              const open = openCh === c.id;
              return (
                <div key={c.id}>
                  <ItemRow
                    hidden={c.hidden}
                    mono={`CH ${c.num}`}
                    title={c.title}
                    sub={`${secs.length} section${secs.length === 1 ? '' : 's'}${c.blurb ? ' — ' + c.blurb : ''}`}
                  >
                    <SwapBar coll="chapters" list={chapters.data} id={c.id} hidden={c.hidden} label={c.title} />
                    <button className="row-btn" onClick={() => setOpenCh(open ? null : c.id)} title="Preview sections">
                      {open ? '▾' : '▸'}
                    </button>
                    <HiddenBtn hidden={!!c.hidden} onClick={() => void toggleChapter(c)} />
                    <button className="row-btn" onClick={() => setView({ id: c.id })} title="Open chapter content editor" style={{ fontWeight: 600 }}>
                      Edit content
                    </button>
                    <DeleteBtn onClick={() => setDeleting({ kind: 'chapter', id: c.id, name: c.title })} />
                  </ItemRow>

                  {open && (
                    <div style={{ margin: '10px 0 4px 22px', paddingLeft: 16, borderLeft: '2px solid var(--line)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span className="mono" style={{ fontSize: 11, letterSpacing: '.1em', color: 'var(--ink3)' }}>SECTIONS</span>
                        <button className="btn btn-ghost btn-sm" onClick={() => setView({ id: c.id })}>Open editor →</button>
                      </div>
                      {secs.length === 0 ? (
                        <p className="hint">No sections yet. Open the editor to add one.</p>
                      ) : (
                        <div className="admin-list">
                          {secs.map((s, si) => (
                            <ItemRow key={s.id} hidden={s.hidden} mono={`SEC ${si + 1}`} title={s.title} sub={s.summary}>
                              <span className={`pill ${resourcesOf(s.id).length ? 'ok' : 'line'}`}>{resourcesOf(s.id).length} files</span>
                              <HiddenBtn hidden={!!s.hidden} onClick={() => void toggleSection(s)} />
                              <EditBtn onClick={() => setView({ id: c.id })} />
                              <DeleteBtn onClick={() => setDeleting({ kind: 'section', id: s.id, name: s.title })} />
                            </ItemRow>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {deleting && (
        <ConfirmDialog
          title={`Delete ${deleting.kind}?`}
          confirmLabel="Delete permanently"
          body={
            <p>
              <strong>{deleting.name}</strong>
              {deleting.kind === 'chapter' && (
                <> — {cascadeNote(chapters.data.find(c => c.id === deleting.id)!)} This cascades and cannot be undone.</>
              )}
              {deleting.kind === 'section' && <> — its resources are deleted too. This cannot be undone.</>}
              {deleting.kind === 'resource' && <> — this cannot be undone.</>}
            </p>
          }
          onConfirm={confirmDelete}
          onCancel={() => setDeleting(null)}
        />
      )}
    </div>
  );
}
